import { Head, Link, usePage } from '@inertiajs/react';
import { BlogLayout } from './BlogLayout';

// Types
interface Post {
  id: number;
  title: string;
  slug: string;
  summary: string;
  content: string;
  coverImageUrl: string | null;
  publishedAt: string | null;
  readingTimeMinutes: number;
  isPremium: boolean;
  author: {
    id: number;
    name: string;
    avatarUrl: string | null;
  } | null;
}

interface PageProps {
  post: Post;
  requiresAuth: boolean;
  requiresPremium: boolean;
  preview: boolean;
}

// Helper function to format date
function formatDate(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

// Get first paragraph from content
function getFirstParagraph(content: string): string {
  if (!content) return '';
  const paragraphs = content.split(/\n\n+/);
  return paragraphs[0] || '';
}

// Lock Icon
function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

// Star Icon (for Premium)
function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

// Main Paywall Page
export default function Paywall() {
  const { post, requiresAuth, requiresPremium, preview } = usePage<PageProps>().props;
  
  const firstParagraph = getFirstParagraph(post.content);

  return (
    <BlogLayout>
      <Head>
        <title>{post.title} - Premium Content</title>
        <meta name="description" content={post.summary} />
      </Head>

      <div className="max-w-3xl mx-auto">
        {/* Premium Header Banner */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-t-xl p-6 text-white">
          <div className="flex items-center justify-center gap-2 mb-2">
            {requiresPremium ? (
              <>
                <StarIcon className="w-6 h-6" />
                <span className="text-lg font-semibold">Premium Content</span>
                <StarIcon className="w-6 h-6" />
              </>
            ) : (
              <>
                <LockIcon className="w-6 h-6" />
                <span className="text-lg font-semibold">Members Only</span>
              </>
            )}
          </div>
          <p className="text-center text-amber-100">
            {requiresPremium 
              ? 'This exclusive content is available to premium members.' 
              : 'Sign in to access this members-only content.'}
          </p>
        </div>

        {/* Post Preview Card */}
        <div className="bg-card dark:bg-card text-foreground rounded-b-xl shadow-lg border border-t-0 border-border overflow-hidden">
          {/* Cover Image */}
          {post.coverImageUrl && (
            <div className="aspect-video relative overflow-hidden">
              <img 
                src={post.coverImageUrl} 
                alt={post.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                {post.isPremium && (
                  <span className="inline-flex items-center gap-1 bg-amber-500 text-white text-xs font-medium px-2 py-1 rounded-full mb-2">
                    <StarIcon className="w-3 h-3" />
                    Premium
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="p-6">
            {/* Title */}
            <h1 className="text-3xl font-bold text-foreground mb-4">
              {post.title}
            </h1>

            {/* Summary */}
            {post.summary && (
              <p className="text-lg text-muted-foreground mb-6">
                {post.summary}
              </p>
            )}

            {/* Author & Date */}
            <div className="flex items-center space-x-4 mb-6 text-sm text-muted-foreground">
              {post.author && (
                <div className="flex items-center space-x-2">
                  {post.author.avatarUrl ? (
                    <img 
                      src={post.author.avatarUrl} 
                      alt={post.author.name}
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                      <span className="text-indigo-600 font-medium text-sm">
                        {post.author.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <span className="font-medium text-foreground">{post.author.name}</span>
                </div>
              )}
              <span>•</span>
              <span>{formatDate(post.publishedAt)}</span>
              <span>•</span>
              <span>{post.readingTimeMinutes} min read</span>
            </div>

            {/* Preview Content */}
            {preview && firstParagraph && (
              <div className="mb-6">
                <div className="bg-muted text-foreground rounded-lg p-4 italic">
                  {firstParagraph}
                </div>
              </div>
            )}

            {/* Paywall CTA */}
            <div className="bg-gradient-to-b from-muted to-background rounded-xl p-6 border border-border text-center">
              {/* Lock Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                  {requiresPremium ? (
                    <StarIcon className="w-8 h-8 text-amber-600" />
                  ) : (
                    <LockIcon className="w-8 h-8 text-amber-600" />
                  )}
                </div>
              </div>

              <h2 className="text-xl font-bold text-foreground mb-2">
                {requiresPremium ? 'Upgrade to Premium' : 'Sign In to Continue'}
              </h2>
              
              <p className="text-muted-foreground mb-6">
                {requiresPremium 
                  ? 'Get unlimited access to all premium content, including this article.'
                  : 'Sign in to your account to access this exclusive content.'}
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {requiresPremium ? (
                  <>
                    <Link
                      href="/signup"
                      className="inline-flex items-center justify-center px-6 py-3 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 transition-colors"
                    >
                      Upgrade Now
                    </Link>
                    <Link
                      href="/signin"
                      className="inline-flex items-center justify-center px-6 py-3 bg-background text-foreground font-medium rounded-lg border border-input hover:bg-muted transition-colors"
                    >
                      Sign In
                    </Link>
                  </>
                ) : (
                  <Link
                    href={`/signin?return_to=${encodeURIComponent(`/blog/${post.slug}`)}`}
                    className="inline-flex items-center justify-center px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Sign In to Continue
                  </Link>
                )}
              </div>

              {/* Benefits List (for Premium) */}
              {requiresPremium && (
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-sm font-medium text-muted-foreground mb-3">Premium benefits include:</p>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Unlimited access to all premium articles
                    </li>
                    <li className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Exclusive member-only content
                    </li>
                    <li className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Support independent publishing
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* Back to Blog Link */}
            <div className="mt-6 text-center">
              <Link
                href="/blog"
                className="text-primary hover:text-primary/80 font-medium"
              >
                ← Browse all articles
              </Link>
            </div>
          </div>
        </div>
      </div>
    </BlogLayout>
  );
}
